import { Icon, IconName } from '@usealloy/component-library';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';

import { Sidebar } from '../component/Sidebar';
import { useLocalStorage } from '../component/hooks/useLocalStorage';
import {
  isAlertQueueActive,
  isEntityEvaluationActive,
  isPrefixActive,
} from './routeMatchers';

// ============================================================
//     COMPONENTS
// ============================================================
/**
 * "Records" nav group — Entities, Evaluations, Alerts, Audit, and API Logs.
 */
export const RecordsGroup = () => {
  const {
    agent, customer, featureFlags, permissions,
  } = useContext(UserContext);
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useLocalStorage('SideNav.Records.isOpen', true, {
    isValid: value => typeof value === 'boolean',
  });

  const hasEntitiesPermission = !!permissions?.['READ:Entities:null'];
  const canViewApiLogs = !!agent?.isEmployee
    && !!permissions?.['READ:ApiLogs:metadata']
    && !!featureFlags?.apiLogsViewer;

  if (!hasEntitiesPermission && !canViewApiLogs) return null;

  const isAdminPath = window?.location?.pathname?.startsWith('/v3/dashboard/admin/') ?? false;

  const canAudit = (customer?.agentAuditEnabled && permissions?.['READ:AuditActions:null'])
    || agent?.isEmployee;
  const showJourneysLink = featureFlags?.journeys && permissions?.['READ:Journey:journey_id'];
  const showAppQueueLink = showJourneysLink && permissions?.['READ:JourneyApplications:null'];
  const showAlertsLink = showAppQueueLink && featureFlags?.alertGenerationInJourneys;
  const isEntitiesActive = isEntityEvaluationActive(
    routes.entities.list,
    pathname,
  );
  const isEvaluationsActive = isEntityEvaluationActive(
    routes.v3.dashboard.evaluations.list,
    pathname,
  );
  const isAlertsActive = isAlertQueueActive(
    routes.v3.dashboard.journeys.list,
    pathname,
  );
  const isAuditActive = isPrefixActive(
    routes.v3.dashboard.audit.list,
    pathname,
  );
  // Always false in practice — this hard-links out to the frontend-dashboard SPA, so Daffy's
  // own router (and this component) is never mounted while actually on that page.
  const isApiLogsActive = isPrefixActive(
    routes.beta.apiLogs.list,
    pathname,
  );

  const entitiesHref = `${window.location.origin}${routes.entities.list}`;
  const evaluationsHref = `${window.location.origin}${routes.v3.dashboard.evaluations.list}`;
  const alertsHref = `${window.location.origin}${routes.v3.dashboard.journeys.alerts.list}`;
  const auditHref = `${window.location.origin}${routes.v3.dashboard.audit.list}`;
  const apiLogsHref = `${window.location.origin}${routes.beta.apiLogs.list}`;

  return (
    <Sidebar.Group collapsible onOpenChange={setIsOpen} open={isOpen}>
      <Sidebar.GroupLabel>
        <span className="fs-unmask">Records</span>
      </Sidebar.GroupLabel>
      <Sidebar.GroupContent>
        <Sidebar.Menu>
          {hasEntitiesPermission && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as="a"
                data-dd-action-name="alloydd-sidebar-nav-entities"
                href={entitiesHref}
                isActive={isEntitiesActive}
                tooltip="Entities"
              >
                <Icon icon={IconName.Entities} size="large" />
                <span className="fs-unmask">Entities</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-evaluations"
              href={evaluationsHref}
              isActive={isEvaluationsActive}
              tooltip="Evaluations"
            >
              <Icon icon={IconName.BalanceScale} size="large" />
              <span className="fs-unmask">Evaluations</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          {showAlertsLink && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as="a"
                data-dd-action-name="alloydd-sidebar-nav-alerts"
                href={alertsHref}
                isActive={isAlertsActive}
                tooltip="Alerts"
              >
                <Icon icon={IconName.BoltAlert} size="large" />
                <span className="fs-unmask">Alerts</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}

          {canAudit && !isAdminPath && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as="a"
                data-dd-action-name="alloydd-sidebar-nav-audit"
                href={auditHref}
                isActive={isAuditActive}
                tooltip="Audit"
              >
                <Icon icon={IconName.FileUser} size="large" />
                <span className="fs-unmask">Audit</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}

          {canViewApiLogs && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as="a"
                data-dd-action-name="alloydd-sidebar-nav-api-logs"
                href={apiLogsHref}
                isActive={isApiLogsActive}
                tooltip="API Logs"
              >
                <Icon icon={IconName.List} size="large" />
                <span className="fs-unmask">API Logs</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}
        </Sidebar.Menu>
      </Sidebar.GroupContent>
    </Sidebar.Group>
  );
};
